import { useState } from 'react';
import { api } from '@/api/client';
import { AlertCircle, Loader2, Upload, X } from 'lucide-react';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import { normalizeCoffeeConstrainedFields } from '@/lib/coffeeValidation';

export default function RequestChangeModal({ review, coffee, user, onClose, onSubmit }) {
  useBodyScrollLock();
  const [requestType, setRequestType] = useState('edit_coffee');
  const [reason, setReason] = useState('');
  const [proposedCoffeeName, setProposedCoffeeName] = useState(coffee?.name || '');
  const [proposedBeanOrigin, setProposedBeanOrigin] = useState(coffee?.bean_origin || '');
  const [proposedVarietal, setProposedVarietal] = useState(coffee?.varietal || '');
  const [proposedRoasterName, setProposedRoasterName] = useState(coffee?.roaster_name || '');
  const [proposedBrewMethod, setProposedBrewMethod] = useState(coffee?.brew_method || '');
  const [proposedProcessingMethod, setProposedProcessingMethod] = useState(coffee?.processing_method || '');
  const [proposedAvailability, setProposedAvailability] = useState(coffee?.availability || 'Regular');
  const [proposedRecipes, setProposedRecipes] = useState(coffee?.recipes || '');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(coffee?.photo_url || null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    setError('');

    try {
      let target_type, target_id, action, proposed_changes;

      if (requestType === 'delete_coffee') {
        target_type = 'coffee';
        target_id = coffee.id;
        action = 'delete';
        proposed_changes = {};
      } else if (requestType === 'edit_coffee') {
        target_type = 'coffee';
        target_id = coffee.id;
        action = 'edit';
        let photo_url = coffee?.photo_url || '';
        if (photoFile) {
          const { file_url } = await api.integrations.Core.UploadFile({ file: photoFile });
          photo_url = file_url;
        }
        // The constrained selects hold '' until something is picked, and ''
        // fails the CHECK constraint on those columns. Storing it here would
        // only surface later, as an approval that fails in the admin dashboard.
        proposed_changes = normalizeCoffeeConstrainedFields({
          name: proposedCoffeeName,
          bean_origin: proposedBeanOrigin,
          varietal: proposedVarietal,
          roaster_name: proposedRoasterName,
          brew_method: proposedBrewMethod,
          processing_method: proposedProcessingMethod,
          availability: proposedAvailability,
          recipes: proposedRecipes,
          photo_url,
        });
      }

      await api.entities.CoffeeChangeRequest.create({
        target_type,
        target_id,
        action,
        requester_id: user.id,
        requester_name: user.full_name || 'Anonymous',
        reason: reason.trim(),
        proposed_changes,
        status: 'pending',
      });
      onSubmit();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isDeleteCoffee = requestType === 'delete_coffee';
  const isEditCoffee = requestType === 'edit_coffee';

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card rounded-t-2xl md:rounded-2xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto overscroll-contain p-6 space-y-5">
        <h3 className="font-heading font-semibold text-lg">Request Change</h3>

        {/* Request type selector */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">What would you like to do?</p>
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'delete_coffee', label: 'Delete Coffee Info' },
              { key: 'edit_coffee', label: 'Edit Coffee Info' },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setRequestType(opt.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  requestType === opt.key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Delete Coffee confirmation */}
        {isDeleteCoffee && (
          <div className="bg-destructive/10 rounded-xl p-4 space-y-2">
            <p className="text-sm text-destructive font-medium">You are requesting to delete this coffee info.</p>
            <p className="text-xs text-muted-foreground">⚠️ All reviews attached to this coffee will also be permanently deleted.</p>
            <p className="text-xs text-muted-foreground">An admin will review your request.</p>
          </div>
        )}

        {/* Edit Coffee fields */}
        {isEditCoffee && (
          <div className="space-y-3 bg-muted/50 rounded-xl p-4">
            <p className="text-xs font-medium text-muted-foreground">Proposed changes to coffee info:</p>
            <div className="space-y-2">
              <div>
                <p className="text-[10px] text-muted-foreground mb-1">Coffee Name</p>
                <input value={proposedCoffeeName} onChange={(e) => setProposedCoffeeName(e.target.value)} className="field-input" />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground mb-1">Recipes</p>
                <textarea value={proposedRecipes} onChange={(e) => setProposedRecipes(e.target.value)} className="field-input resize-none" rows={2} placeholder="e.g. espresso shot, oat milk, vanilla syrup..." />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Origin</p>
                  <input value={proposedBeanOrigin} onChange={(e) => setProposedBeanOrigin(e.target.value)} className="field-input" placeholder="e.g. Ethiopia" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Varietal</p>
                  <input value={proposedVarietal} onChange={(e) => setProposedVarietal(e.target.value)} className="field-input" placeholder="e.g. Geisha" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Roaster</p>
                  <input value={proposedRoasterName} onChange={(e) => setProposedRoasterName(e.target.value)} className="field-input" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Brew Method</p>
                  <select value={proposedBrewMethod} onChange={(e) => setProposedBrewMethod(e.target.value)} className="field-input">
                    <option value="">—</option>
                    {['Espresso', 'Hand drip', 'Aeropress', 'French press', 'Cold brew'].map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Processing</p>
                  <select value={proposedProcessingMethod} onChange={(e) => setProposedProcessingMethod(e.target.value)} className="field-input">
                    <option value="">—</option>
                    {['Washed', 'Natural', 'Honey', 'Other'].map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Availability</p>
                  <select value={proposedAvailability} onChange={(e) => setProposedAvailability(e.target.value)} className="field-input">
                    <option value="Regular">Regular</option>
                    <option value="Limited time">Limited time</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Photo */}
            <div>
              <p className="text-[10px] text-muted-foreground mb-1">Coffee Photo</p>
              {photoPreview ? (
                <div className="relative inline-block">
                  <img src={photoPreview} alt="preview" className="max-h-40 max-w-[200px] object-contain rounded-xl border border-border" />
                  <button
                    onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                    className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center text-white"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center gap-1.5 border-2 border-dashed border-border rounded-xl p-4 cursor-pointer hover:border-primary/50 transition-colors">
                  <Upload size={18} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Tap to upload a photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setPhotoFile(file);
                      setPhotoPreview(URL.createObjectURL(file));
                    }}
                  />
                </label>
              )}
            </div>
          </div>
        )}

        {/* Reason */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">
            Reason <span className="text-destructive">*</span>
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="field-input min-h-[60px]"
            placeholder={isDeleteCoffee ? 'Why should this coffee info be removed?' : 'Why do you want to make this change?'}
          />
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-xs text-destructive">
            <AlertCircle size={14} className="shrink-0 mt-px" />
            <span>{error}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !reason.trim()}
            className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Submit Request
          </button>
        </div>
      </div>
    </div>
  );
}